import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';
import { CustomerContractInventoryGridItem } from 'shared/types/customerManagement';
import { useCustomerContractSummaryCustomerContractListState } from '../state/customerContractSummaryCustomerContractListState';

const containerStyles: SxProps<Theme> = {
  maxHeight: '300px',
  minHeight: '100px',
};

const sortStyles: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
};

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

interface CustomerContractTableProps {
  instance: TableInstance<CustomerContractInventoryGridItem>;
}
export function CustomerContractTable({ instance }: CustomerContractTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = instance;

  const headers = headerGroups.map(headerGroup => (
    <TableRow {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map(column => (
        <TableCell
          align={column.align}
          {...column.getHeaderProps({
            style: {
              width: column.width,
              minWidth: column.minWidth,
            },
          })}
        >
          <Box
            component='span'
            sx={sortStyles}
            {...column.getSortByToggleProps({ style: { justifyContent: getHeaderFlexAlignment(column.align) } })}
          >
            {column.render('Header')}
            {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
          </Box>
        </TableCell>
      ))}
    </TableRow>
  ));
  const rows = page.length
    ? page.map((row, i) => {
        prepareRow(row);
        const { key, ...rest } = row.getRowProps();
        return <CustomerContractTableRow key={key} row={row} {...rest} />;
      })
    : [
        <TableRow key='no-data'>
          <TableCell colSpan={8}>
            <Typography>No contracts have been entered.</Typography>
          </TableCell>
        </TableRow>,
      ];
  return (
    <div>
      <TableContainer sx={containerStyles}>
        <Table size='small' stickyHeader {...getTableProps()}>
          <TableHead>{headers}</TableHead>
          <TableBody {...getTableBodyProps()}>{rows}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

interface CustomerContractTableRowProps extends TableRowProps {
  row: Row<CustomerContractInventoryGridItem>;
}

function CustomerContractTableRow({ row, ...props }: CustomerContractTableRowProps) {
  const state = useCustomerContractSummaryCustomerContractListState();

  const highlight = state.customerId.get() === row.id;
  return (
    <TableRow selected={highlight} {...props}>
      {row.cells.map(cell => (
        <TableCell align={cell.column.align} {...cell.getCellProps()}>
          {cell.render('Cell')}
        </TableCell>
      ))}
    </TableRow>
  );
}
